import { useQuery } from "@tanstack/react-query";
import { Database, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface IndexStatusResponse {
  status: string;
  total_chunks: number;
  fts_chunks: number | null;
  fts_in_sync: boolean | null;
  books_total: number;
  customers_total: number;
  customers: string[];
  unknown_count: number;
  last_reindex: {
    pages_indexed?: number;
    attachments_indexed?: number;
    orphan_chunks_deleted?: number;
    finished_at?: string;
  } | null;
  hybrid_enabled: boolean;
  hyde_enabled: boolean;
}

/** ArkiveAI 인덱싱 상태 종합 위젯. 60초마다 자동 갱신. */
export function IndexStatusCard() {
  const { data, isLoading } = useQuery<IndexStatusResponse>({
    queryKey: ["admin", "index-status"],
    queryFn: () => api.get("/admin/index-status"),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return <Skeleton className="h-48" />;
  }

  const finishedAt = data.last_reindex?.finished_at
    ? new Date(data.last_reindex.finished_at).toLocaleString("ko-KR")
    : "기록 없음";

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Database className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold">인덱싱 상태</h3>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">총 청크</div>
            <div className="text-lg font-semibold">{data.total_chunks.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">FTS 청크 (동기)</div>
            <div className="text-lg font-semibold">
              {data.fts_chunks?.toLocaleString() ?? "-"}
              {data.fts_in_sync === true && <CheckCircle2 className="inline h-4 w-4 ml-1 text-green-600" />}
              {data.fts_in_sync === false && <AlertTriangle className="inline h-4 w-4 ml-1 text-orange-500" />}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">책 수</div>
            <div className="text-lg font-semibold">{data.books_total}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">
              <Users className="inline h-3 w-3 mr-1" />
              고객사 수
            </div>
            <div className="text-lg font-semibold">{data.customers_total}</div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200 text-xs space-y-1">
          <div>
            <span className="text-gray-500">마지막 인덱싱:</span>{" "}
            <span className="font-medium">{finishedAt}</span>
          </div>
          {data.last_reindex && (
            <div className="text-gray-500">
              페이지 {data.last_reindex.pages_indexed} · 첨부 {data.last_reindex.attachments_indexed} ·
              orphan 정리 {data.last_reindex.orphan_chunks_deleted}
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <span className={data.hybrid_enabled ? "text-green-600" : "text-gray-400"}>
              {data.hybrid_enabled ? "✓" : "○"} Hybrid
            </span>
            <span className={data.hyde_enabled ? "text-green-600" : "text-gray-400"}>
              {data.hyde_enabled ? "✓" : "○"} HyDE
            </span>
          </div>
        </div>

        {data.unknown_count > 0 && (
          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
            <AlertTriangle className="inline h-3 w-3 mr-1" />
            미분류 책 {data.unknown_count}건 — 책장 배치 필요
          </div>
        )}
      </CardContent>
    </Card>
  );
}
